import { useParams, useNavigate, useLocation } from 'react-router-dom'

export interface UseRoutedTabsProps {
  basePath: string // e.g., '/organization/sso'
  validTabs: string[]
  defaultTab?: string // If not provided, uses first tab
  /**
   * Carry the query string across a tab switch. Off by default: where each tab
   * keeps its own table state in the URL (People's Members and Invitations both
   * read `?search` and `?page`), a switch must start the next tab clean. Turn
   * it on when every param belongs to one tab — the SSO page's audit sort — so
   * leaving that tab and coming back finds it as it was.
   */
  keepSearch?: boolean
}

export function useRoutedTabs({ basePath, validTabs, defaultTab, keepSearch = false }: UseRoutedTabsProps) {
  const { tab } = useParams<{ tab: string }>()
  const navigate = useNavigate()
  const { search } = useLocation()

  const fallbackTab = defaultTab || validTabs[0]

  // Derive the active tab directly from the URL route parameter.
  // This makes the URL the single source of truth and prevents state-related race conditions.
  const activeTab = (tab && validTabs.includes(tab)) ? tab : fallbackTab

  // This handler now only dispatches a navigation event.
  // The component will re-render naturally when the route changes.
  const handleTabChange = (newTab: string) => {
    if (newTab === activeTab) return

    const query = keepSearch ? search : ''
    if (newTab === fallbackTab) {
      navigate(`${basePath}${query}`)
    } else {
      navigate(`${basePath}/${newTab}${query}`)
    }
  }

  return {
    activeTab,
    handleTabChange
  }
}
